// frontend/src/Components/SyncTo/SyncToButton.jsx
//
// Phase 29 — gated render stub. (Preserved: flag gate returns null when flag=false.)
// Phase 30 — EXTENDED:
//   - D-01: onClick opens <RecordModal> via useRecording().openModal(meta).
//   - D-02: when flag=true BUT pickMimeType()===null, render disabled Button
//           with OverlayTrigger tooltip explaining unsupported browser.
//   - D-03: clicking while a recording is active re-opens the same modal;
//           the provider's live state drives the re-mounted UI uninterrupted.
//   - New optional props: appointmentId, patientLabel (passed through to
//     useRecording().openModal(meta) so the sessions row gets populated).
//   - Phase 32.1: optional `onBeforeOpen` async prop. If provided, awaited
//     before openModal; if it returns { sessionId } the value is threaded
//     into meta, replacing the sessionId prop. Backward compatible when
//     onBeforeOpen is undefined — behaviour is identical to Phase 32 shipped
//     code (single openModal(meta) call with same meta shape).
//
// Render gate: reads user.features.syncTo from the decoded JWT in
// sessionStorage. UX-only; the backend middleware is authoritative
// (backend/src/middleware/requireSyncToFeatureFlag.js).
//
// Phase 34 will replace this onClick with <ModeModal> per SYNC-01 final
// acceptance; Phase 30 keeps the migration boundary clean by opening
// <RecordModal> directly (no ModeModal stub).

import React from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { jwtDecode } from 'jwt-decode';
import { useRecording } from './RecordingProvider';
import { pickMimeType } from './helpers/mimePicker';

function readFlagFromJwt() {
  try {
    const token = sessionStorage.getItem('user');
    if (!token) return false;
    const decoded = jwtDecode(token);
    return !!decoded?.features?.syncTo;
  } catch {
    // Malformed token — render nothing rather than throwing.
    return false;
  }
}

export default function SyncToButton({
  isActive,
  setActiveSection,
  appointmentId,
  patientLabel,
  sessionId,       // Phase 32 — routed to ReviewModal for /apply body
  onApplied,       // Phase 32 — Session.jsx refetch handler
  onBeforeOpen,    // Phase 32.1 — optional async hook; awaited before openModal.
                   // Signature: async () => ({ sessionId } | { sessionId: null, error })
                   // If returned sessionId is truthy, it replaces the sessionId prop in meta.
                   // If returned sessionId is null/undefined (or onBeforeOpen throws),
                   // openModal is NOT called — Session.jsx owns error surfacing.
                   // When undefined, behaviour is identical to Phase 32 shipped code.
  meta,            // Phase 33 — optional meta object forwarded into openModal().
                   // Shape: { mode?: 'note'|'template', templateId?, templatePages?,
                   //          existingCapturedValues? }. When mode is 'template',
                   // Session.jsx (via ModeModal in Phase 34) supplies templatePages
                   // and existingCapturedValues so the ReviewModal Template variant
                   // can render the per-field table + NoteRenderer preview. When
                   // meta is undefined, behaviour is identical to Phase 32
                   // shipped code (mode defaults to 'note').
}) {
  // Hooks must be called unconditionally (rules-of-hooks) — call before any
  // early return. pickMimeType is a pure helper, not a hook, but we group
  // the derived values here for clarity.
  const recording = useRecording();
  const mimeSupported = pickMimeType() !== null;

  // Phase 29 behaviour: flag=false → render nothing.
  if (!readFlagFromJwt()) return null;

  // D-02 — flag=true BUT browser unsupported → disabled + tooltip (SC6).
  // OverlayTrigger cannot fire on a disabled button directly; wrap in a span.
  if (!mimeSupported) {
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id="tooltip-syncto-unsupported">
            Your browser does not support audio recording — please use Chrome, Edge, Firefox, or Safari 14.1+
          </Tooltip>
        }
      >
        <span className="d-inline-block">
          <Button
            variant="outline-secondary"
            className="rounded-0 d-flex align-items-center gap-2"
            style={{ pointerEvents: 'none' }}
            disabled
          >
            <i className="fa-light fa-microphone-lines" />
            SyncTo
          </Button>
        </span>
      </OverlayTrigger>
    );
  }

  // D-01 / D-03 — open RecordModal. If already recording, re-opening the
  // modal is harmless: the provider's state (timer, level, recorder handle)
  // drives the re-mounted UI uninterrupted.
  //
  // Phase 32.1 — if onBeforeOpen is provided, await it before openModal so
  // Session.jsx can lazy-create a session doc (GAP-A / B2). A truthy returned
  // sessionId replaces the prop-passed sessionId in meta. A null/undefined
  // sessionId (or a thrown error) aborts the modal open — Session.jsx is
  // responsible for any user-visible error surfacing; SyncToButton stays
  // UI-pure and does not duplicate that logic.
  async function handleClick() {
    let resolvedSessionId = sessionId;   // default to prop value (backward compat)
    if (typeof onBeforeOpen === 'function') {
      try {
        const result = await onBeforeOpen();
        if (!result || result.sessionId == null) {
          // onBeforeOpen signalled failure (returned null, undefined, or { sessionId: null })
          // Do NOT open the modal. ensureSessionExists in Session.jsx is responsible for any
          // user-visible error messaging; SyncToButton stays UI-pure.
          return;
        }
        resolvedSessionId = result.sessionId;
      } catch (_err) {
        // onBeforeOpen threw — bail silently here (Session.jsx owns error surfacing).
        return;
      }
    }
    recording?.openModal({
      appointmentId,
      patientLabel,
      sessionId: resolvedSessionId,   // threads to Provider meta → ReviewModal props
      mode: meta?.mode || 'note',     // Phase 33 — caller-supplied mode (ModeModal → Phase 34); defaults to 'note'
      templateId: meta?.templateId,   // Phase 33 — template-mode only (undefined for note)
      templatePages: meta?.templatePages,             // Phase 33 — for ReviewModal Template variant
      existingCapturedValues: meta?.existingCapturedValues, // Phase 33 — for NoteRenderer overlay
      onApplied,                      // Session.jsx handler — invoked post-apply-200
    });
  }

  return (
    <Button
      variant={isActive ? 'primary' : 'outline-primary'}
      className="rounded-0 d-flex align-items-center gap-2"
      onClick={handleClick}
    >
      <i className="fa-light fa-microphone-lines" />
      SyncTo
    </Button>
  );
}
